/**
 * @file 事業者（プロバイダ）設定取得フック
 *
 * 現在のURLパスから事業者IDを特定し、対応する設定JSONを動的にインポートして
 * Recoil（providerInfoState）にセットする。
 * 複数FVNO事業者に対応するため、URLプレフィクスと事業者IDのマッピングで制御している。
 */
import { ProviderInfoStateType, providerInfoState } from '@/states/providerInfoState';
import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

/** URLプレフィクスと事業者IDの対応定義 */
type ProviderIdList = {
  providerId: string;
  deploy: boolean;
  urlPrefix: string;
  domain?: string;
};

/** 特定した事業者IDをモジュールスコープで保持（再初期化防止用） */
let providerId: string = '';

/**
 * 事業者設定を取得するカスタムフック
 * @returns execute - URLパスから事業者IDを判定し設定をRecoilに格納する関数
 */
const useGetProviderConfig = () => {
  const setProviderInfo = useSetRecoilState(providerInfoState);
  const providerInfo = useRecoilValue(providerInfoState);

  const execute = useCallback(
    async (pathname: string): Promise<void> => {
      // ログイン・ログアウト・ルートページ以外で既に事業者情報がある場合はスキップ可能
      if (providerInfo && pathname !== '/login' && pathname !== '/logout' && pathname !== '/') {
        // return;
        // Noop
      }

      const url = new URL(window.location.href).pathname;
      // URLパスの先頭部分から事業者IDを特定する
      await import(`@/hooks/provider/urlList.json`).then(async (providerList: ProviderIdList[]) => {
        for (let i = 0; i < providerList.length; i += 1) {
          if (url.startsWith(`/${providerList[i].urlPrefix}`)) {
            providerId = providerList[i].providerId;
            break;
          }
        }

        // 事業者IDに対応する設定JSONを動的インポートしてRecoilにセット
        await import(`@/hooks/provider/config/${providerId}.json`).then((config: ProviderInfoStateType) => {
          setProviderInfo({
            providerId,
            serviceName: config.serviceName,
            companyName: config.companyName,
            pageTitle: config.pageTitle,
            metaDiscription: config.metaDiscription,
            metaKeywords: config.metaKeywords,
            serviceUrl: config.serviceUrl,
            planKiyakuUrl: config.planKiyakuUrl,
            optionKiyakuUrl: config.optionKiyakuUrl,
            optionDetailUrl: config.optionDetailUrl,
            settleLogoFile: config.settleLogoFile,
            jyusetsuUrl: config.jyusetsuUrl,
            supportdeskname: config.supportdeskname,
            supportdesktel: config.supportdesktel,
            supportdeskemploymenttime: config.supportdeskemploymenttime,
            supportdeskemploymentday: config.supportdeskemploymentday,
          });
        });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  return { execute };
};
export default useGetProviderConfig;
